import React from "react";
import { Link } from "react-router-dom";

const Section = ({ title, children }) => (
  <div className="border-b border-slate-200 py-8">
    <h2 className="text-base font-semibold text-slate-900">{title}</h2>
    <div className="mt-3 space-y-3 text-sm leading-relaxed text-slate-600">
      {children}
    </div>
  </div>
);

export default function Privacy() {
  return (
    <div className="fixed inset-0 z-40 overflow-y-auto bg-white text-left">
      <div className="mx-auto max-w-2xl px-4 pb-10 pt-6 md:px-8 md:pb-14">
        <Link
          to="/"
          className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900"
        >
          &larr; Back to Home
        </Link>
        <h1 className="mt-5 text-2xl font-semibold text-slate-900 md:text-3xl">
          Privacy Policy
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Last updated: March 9, 2026
        </p>
      </div>

      <div className="mx-auto max-w-2xl px-4 md:px-8">
        <Section title="Introduction">
          <p>
            Kakeibo ("we", "our", or "us") operates the Kakeibo web application
            and mobile application (collectively, the "Service"). This Privacy
            Policy explains how we collect, use, and protect your personal
            information when you use our Service.
          </p>
          <p>
            By using Kakeibo, you agree to the collection and use of
            information in accordance with this policy.
          </p>
        </Section>

        <Section title="Information We Collect">
          <p>We collect the following types of information:</p>
          <ul className="list-disc pl-4 space-y-1 text-left">
            <li>
              <strong>Account Information:</strong> Email address and password
              when you create an account.
            </li>
            <li>
              <strong>Profile Information:</strong> Optional first name and last
              name if you choose to provide them in your settings.
            </li>
            <li>
              <strong>Financial Data:</strong> Income, expense, and savings
              entries that you manually input into the application. Kakeibo does
              not connect to bank accounts, financial institutions, or any
              external financial services. All financial data in the application
              is entered directly by you.
            </li>
            <li>
              <strong>Usage Data:</strong> Basic analytics about how you
              interact with the Service, such as pages visited and features
              used.
            </li>
          </ul>
        </Section>

        <Section title="How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul className="list-disc pl-4 space-y-1 text-left">
            <li>Provide, maintain, and improve the Service.</li>
            <li>
              Generate budgeting reports and insights based on your entries.
            </li>
            <li>Authenticate your identity and secure your account.</li>
            <li>Send important service-related communications.</li>
          </ul>
        </Section>

        <Section title="Data Ownership and User Control">
          <p>
            You retain full ownership of the financial data you enter into
            Kakeibo. We use your data solely to provide the core Service
            functionality, including budgeting reports, spending summaries, and
            savings insights. We do not use your financial data for any purpose
            unrelated to delivering the Service to you.
          </p>
        </Section>

        <Section title="Cookies and Tracking Technologies">
          <p>
            We may use cookies and similar technologies to support
            authentication, maintain your session, and understand how the
            Service is used. These technologies help us improve functionality
            and user experience.
          </p>
          <p>
            Third-party services integrated into the Service (such as analytics
            or advertising providers) may also use cookies or similar
            technologies. You can manage cookie preferences through your
            browser or device settings.
          </p>
        </Section>

        <Section title="Legal Basis for Processing (GDPR)">
          <p>
            If you are located in the European Economic Area (EEA), we process
            your personal data under the following legal bases:
          </p>
          <ul className="list-disc pl-4 space-y-1 text-left">
            <li>
              <strong>Contractual necessity:</strong> Processing required to
              provide you with the Service (e.g., managing your account and
              generating budgeting reports).
            </li>
            <li>
              <strong>Legitimate interests:</strong> Processing necessary for
              security, fraud prevention, service maintenance, and improvement.
            </li>
            <li>
              <strong>Consent:</strong> Where required, such as for optional
              analytics or personalized advertising.
            </li>
          </ul>
          <p>
            You may withdraw your consent at any time where consent is the
            basis for processing. This will not affect the lawfulness of any
            processing carried out before withdrawal.
          </p>
        </Section>

        <Section title="Data Storage and Security">
          <p>
            Your data is stored on secure servers. All data transmitted between
            your device and our servers is protected using HTTPS encryption. We
            apply reasonable technical and organizational safeguards to protect
            your personal information from unauthorized access, alteration, or
            loss.
          </p>
          <p>
            While we take data protection seriously and work to maintain strong
            safeguards, no method of electronic transmission or storage can be
            guaranteed to be 100% secure.
          </p>
        </Section>

        <Section title="International Data Transfers">
          <p>
            Your information may be processed or stored on servers located
            outside your country of residence. Where such transfers occur, we
            take reasonable steps to ensure that your data is treated securely
            and in accordance with this Privacy Policy, including using
            appropriate safeguards where required by applicable law.
          </p>
        </Section>

        <Section title="Third-Party Services">
          <p>We use the following third-party services:</p>
          <ul className="list-disc pl-4 space-y-1 text-left">
            <li>
              <strong>Google AdMob</strong> (mobile app only): We display
              advertisements through Google AdMob in the mobile application.
              AdMob may collect certain information to serve and measure ads,
              including device identifiers, IP address, app usage and
              interaction data, and advertising performance metrics. This data
              is collected and processed by Google in accordance with{" "}
              <a
                href="https://policies.google.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-900 underline hover:text-slate-700"
              >
                Google's Privacy Policy
              </a>
              . AdMob data collection applies to the mobile app only and does
              not apply to the web application.
            </li>
            <li>
              <strong>Google Sign-In:</strong> If you choose to sign in with
              Google, we receive your email address and basic profile
              information (such as your name and profile picture) associated
              with your Google account. We do not receive your Google account
              password or access to other Google services.
            </li>
          </ul>
        </Section>

        <Section title="Data Sharing">
          <p>
            We do not sell, trade, or rent your personal information to third
            parties. We may share data only in the following circumstances:
          </p>
          <ul className="list-disc pl-4 space-y-1 text-left">
            <li>With your explicit consent.</li>
            <li>To comply with legal obligations or enforce our policies.</li>
            <li>
              With service providers who assist in operating the Service (e.g.,
              hosting providers), under strict confidentiality agreements.
            </li>
          </ul>
        </Section>

        <Section title="Data Retention">
          <p>
            We retain your account information and financial data for as long
            as your account is active and as needed to provide you with the
            Service.
          </p>
          <p>
            If you delete your account, we will delete your personal data
            within 30 days, unless we are required to retain certain
            information for legal, fraud-prevention, or security purposes.
          </p>
          <p>
            Anonymous or aggregated data that can no longer identify you may be
            retained for analytical purposes.
          </p>
        </Section>

        <Section title="Your Rights">
          <p>You have the right to:</p>
          <ul className="list-disc pl-4 space-y-1 text-left">
            <li>Access and view all data associated with your account.</li>
            <li>
              Update or correct your personal information through the Settings
              page.
            </li>
            <li>
              Request deletion of your account and all associated data by
              contacting us at{" "}
              <a
                href="mailto:support@ymym.io"
                className="text-slate-900 underline hover:text-slate-700"
              >
                support@ymym.io
              </a>
              .
            </li>
            <li>
              Request a copy of the personal data we hold about you.
            </li>
            <li>Opt out of non-essential communications.</li>
          </ul>
          <p>
            We will respond to data-related requests within a reasonable
            timeframe and in accordance with applicable law.
          </p>
        </Section>

        <Section title="Children's Privacy">
          <p>
            Our Service is not intended for use by children under the age of
            13. We do not knowingly collect personal information from children
            under 13. If we discover that a child under 13 has provided us with
            personal data, we will delete it promptly.
          </p>
        </Section>

        <Section title="Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. We will notify
            you of any changes by posting the new Privacy Policy on this page
            and updating the "Last updated" date.
          </p>
        </Section>

        <div className="py-8">
          <h2 className="text-base font-semibold text-slate-900">
            Contact Us
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-slate-600">
            If you have any questions about this Privacy Policy or your data,
            please contact us at{" "}
            <a
              href="mailto:support@ymym.io"
              className="text-slate-900 underline hover:text-slate-700"
            >
              support@ymym.io
            </a>
            .
          </p>
        </div>
      </div>

      <footer className="mx-auto max-w-2xl border-t border-slate-200 px-4 py-6 text-xs text-slate-400 md:px-8">
        &copy; {new Date().getFullYear()} Kakeibo. All rights reserved.
      </footer>
    </div>
  );
}
